import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  UseFormClearErrors,
  FieldErrors,
} from 'react-hook-form';

import { ROUTE_DEFINITIONS } from '@/routes';
import { monthlyCutsService } from '@/api/domains/attendance';
import { monthlyCutsYearsService } from '@/api/domains/catalog/monthlyCutsYears/monthlyCutsYears.service';
import { useHoldingCompanies } from '@/api/domains/catalog/holding/hooks/useHoldingCompanies';
import { useAppSelector } from '@/hooks';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useTranslation } from '@/i18n';
import { getMonthOptions } from '@/utils/dateRange';
import { processBackendValidationErrors } from '@/utils/formValidationErrors';
import {
  createMonthlyCutSchema,
  type CreateMonthlyCutFormData,
} from '@/schemas/attendance/createMonthlyCutForm';
import {
  useMonthlyCutGroupsTransfer,
  type UseMonthlyCutGroupsTransferResult,
} from './useMonthlyCutGroupsTransfer';


export type MonthlyCutTabId = 'general' | 'agrupacion';

// Campos de la pestaña General (para enrutar el foco a la pestaña con error).
const GENERAL_FIELDS: (keyof CreateMonthlyCutFormData)[] = [
  'targetCompanyId', 'year', 'month', 'startDate', 'endDate',
];

export interface CreateMonthlyCutFormSubset {
  register: UseFormRegister<CreateMonthlyCutFormData>;
  setValue: UseFormSetValue<CreateMonthlyCutFormData>;
  watch: UseFormWatch<CreateMonthlyCutFormData>;
  trigger: UseFormTrigger<CreateMonthlyCutFormData>;
  clearErrors: UseFormClearErrors<CreateMonthlyCutFormData>;
  errors: FieldErrors<CreateMonthlyCutFormData>;
}

export interface UseCreateMonthlyCutResult {
  permissionsLoaded: boolean;
  canCreate: boolean;
  screenCode: string;
  isLoading: boolean;
  isSubmitting: boolean;
  activeTab: MonthlyCutTabId;
  setActiveTab: (tab: MonthlyCutTabId) => void;
  form: CreateMonthlyCutFormSubset;
  companyOptions: { value: string; label: string }[];
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  groups: UseMonthlyCutGroupsTransferResult;
  handleCancel: () => void;
  handleCreate: () => void;
}

export function useCreateMonthlyCut(): UseCreateMonthlyCutResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showWarning, showApiError, showNotification } = useUI();
  const navigate = useNavigate();
  const screenCode = ROUTE_DEFINITIONS.CORTE_MENSUAL.code ?? '';
  const { isLoaded: permissionsLoaded, canCreate } = usePagePermissions(screenCode);
  // Solo cargar catálogos/agrupaciones cuando el permiso está resuelto y el usuario puede crear:
  // evita requests innecesarios y toasts de error al entrar por URL directa sin permiso
  // (mismo gate que `enabled: permissionsLoaded && canView` en los list hooks).
  const canLoadData = permissionsLoaded && canCreate;
  // Empresa del usuario logueado: se usa como targetCompanyId en modo holding.
  const currentCompanyId = useAppSelector((state) => state.auth.user?.idEmpresa ?? null);

  const [activeTab, setActiveTab] = useState<MonthlyCutTabId>('general');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<CreateMonthlyCutFormData>({
    resolver: zodResolver(createMonthlyCutSchema),
    mode: 'onSubmit',
    defaultValues: {
      targetCompanyId: null,
      applyToAllCompanies: false,
      year: null,
      month: null,
      startDate: '',
      endDate: '',
      groupIds: [],
    },
  });

  // ── Catálogos ───────────────────────────────────────────────────────────
  const { companies, loading: companiesLoading } = useHoldingCompanies({ enabled: canLoadData });
  const companyOptions = useMemo(
    () => companies.map((company) => ({ value: String(company.id), label: company.name })),
    [companies],
  );

  const [years, setYears] = useState<number[]>([]);
  const [yearsLoading, setYearsLoading] = useState(true);
  useEffect(() => {
    if (!canLoadData) return;
    const controller = new AbortController();
    const loadYears = async () => {
      setYearsLoading(true);
      const response = await monthlyCutsYearsService.getYears(controller.signal);
      if (response.error?.code === 'ABORTED') return;
      if (response.success && response.data) {
        setYears(response.data.map((item) => item.year));
      } else {
        showApiError(response, translate('attendance:monthlyCutCreate.messages.yearsLoadError'));
      }
      setYearsLoading(false);
    };
    void loadYears();
    return () => controller.abort();
  }, [canLoadData, showApiError, translate]);

  const yearOptions = useMemo(
    () => years.map((year) => ({ value: String(year), label: String(year) })),
    [years],
  );

  const monthOptions = useMemo(() => getMonthOptions(translate), [translate]);

  // ── Agrupación (transfer panel) ─────────────────────────────────────────
  const groups = useMonthlyCutGroupsTransfer({ enabled: canLoadData });

  // Mantiene el campo del formulario sincronizado con la lista de asignadas.
  useEffect(() => {
    methods.setValue('groupIds', groups.assignedIds);
    if (groups.assignedIds.length > 0) {
      methods.clearErrors('groupIds');
    }
  }, [groups.assignedIds, methods]);

  // ── Submit ──────────────────────────────────────────────────────────────
  const fieldMapping = useMemo(() => {
    const formInstance = { setError: methods.setError };
    return {
      year: { tab: 'general' as MonthlyCutTabId, formInstance },
      month: { tab: 'general' as MonthlyCutTabId, formInstance },
      startDate: { tab: 'general' as MonthlyCutTabId, formInstance },
      endDate: { tab: 'general' as MonthlyCutTabId, formInstance },
      targetCompanyId: { tab: 'general' as MonthlyCutTabId, formInstance },
      applyToAllCompanies: { tab: 'general' as MonthlyCutTabId, formInstance },
      groupIds: { tab: 'agrupacion' as MonthlyCutTabId, formInstance },
    };
  }, [methods]);

  const handleCancel = useCallback(() => {
    void navigate(ROUTE_DEFINITIONS.CORTE_MENSUAL.path);
  }, [navigate]);

  const handleCreate = useCallback(async () => {
    // Garantiza que groupIds refleje el estado actual del panel antes de validar.
    methods.setValue('groupIds', groups.assignedIds);

    const generalValid = await methods.trigger(GENERAL_FIELDS);
    if (!generalValid) {
      setActiveTab('general');
      return;
    }
    const groupsValid = await methods.trigger('groupIds');
    if (!groupsValid) {
      setActiveTab('agrupacion');
      return;
    }

    setIsSubmitting(true);
    const values = methods.getValues();
    // En holding la empresa que se envía es SIEMPRE la del usuario logueado (la empresa desde
    // la que se solicita el corte); el backend resuelve la empresa padre internamente. Aunque
    // el selector haya conservado un valor antes de activar el switch, en holding no se usa.
    const effectiveTargetCompanyId = values.applyToAllCompanies
      ? currentCompanyId
      : values.targetCompanyId;

    const response = await monthlyCutsService.create({
      targetCompanyId: effectiveTargetCompanyId,
      applyToAllCompanies: values.applyToAllCompanies,
      year: values.year as number,
      month: values.month as number,
      startDate: values.startDate,
      endDate: values.endDate,
      groupIds: values.groupIds,
    });

    if (!response.success) {
      if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
        processBackendValidationErrors(
          response.error.validationErrors,
          fieldMapping,
          (tab) => setActiveTab(tab),
        );
        // Notificación genérica para garantizar feedback aunque ningún fieldName calce con el
        // fieldMapping (p. ej. diferencia de casing del backend); evita que Guardar "no haga nada".
        showNotification('error', translate('common:validation.formErrors'));
        setIsSubmitting(false);
        return;
      }
      showApiError(response, translate('attendance:monthlyCutCreate.messages.createError'));
      setIsSubmitting(false);
      return;
    }

    showSuccess(translate('attendance:monthlyCutCreate.messages.createSuccess'));
    // Solo alertar cuando la empresa SÍ tiene sincronización con REX+ y aun así no se sincronizó.
    if (response.data && response.data.rexApplicable && !response.data.rexSynced) {
      showWarning(translate('attendance:monthlyCutCreate.messages.rexNotSynced'));
    }
    void navigate(ROUTE_DEFINITIONS.CORTE_MENSUAL.path);
  }, [
    methods, groups.assignedIds, fieldMapping, currentCompanyId,
    showApiError, showSuccess, showWarning, showNotification, translate, navigate,
  ]);

  const form: CreateMonthlyCutFormSubset = {
    register: methods.register,
    setValue: methods.setValue,
    watch: methods.watch,
    trigger: methods.trigger,
    clearErrors: methods.clearErrors,
    errors: methods.formState.errors,
  };

  return {
    permissionsLoaded,
    canCreate,
    screenCode,
    isLoading: companiesLoading || yearsLoading,
    isSubmitting,
    activeTab,
    setActiveTab,
    form,
    companyOptions,
    yearOptions,
    monthOptions,
    groups,
    handleCancel,
    handleCreate,
  };
}

export default useCreateMonthlyCut;
